import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { VStack, Box, HStack, Text, Button, Icon, useDisclosure } from '@chakra-ui/react';
import { FiScissors, FiZap, FiMap, FiCalendar, FiPlus, FiUsers } from 'react-icons/fi'
import { useDailyReview, useCampaignTurfs, useCampaignDetail } from '../../../hooks/useCampaign'
import { ItineraryBuilder } from '../itinerary'
import { CampaignAutoGenerateModal } from '../CampaignAutoGenerateModal'
import { MergeListModal } from '../MergeListModal'
import { TurfReviewMapModal } from '../dailyReview/TurfReviewMapModal'
import { TurfMapViewerDrawer } from '../TurfMapViewerDrawer'
import { CanvassThisButton } from '../CanvassThisButton'
import { GenerationChangeBanner } from './GenerationChangeBanner'
import { ActiveTurfsTable } from './ActiveTurfsTable'

interface Props {
  campaignId: string
  campaignLabel: string
  /** Renders the Itinerary Builder in a non-interactive (no drag-and-drop) mode. Default false. */
  readOnly?: boolean
}

function todayStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function PlanningSection({ campaignId, campaignLabel, readOnly = false }: Props) {
  const navigate = useNavigate()
  const { data: review } = useDailyReview(campaignId, todayStr())
  const { data: turfs } = useCampaignTurfs(campaignId)
  const { data: campaignDetail } = useCampaignDetail(campaignId)
  const autoGenerateModal = useDisclosure()
  const mergeListModal = useDisclosure()
  const recutModal = useDisclosure()
  const turfMapDrawer = useDisclosure()
  const [viewTurfMapId, setViewTurfMapId] = useState<string | null>(null)
  // [Canvass this] set by the button's onReady; opens the recut modal on Today with the Skip strip.
  const [canvassThisMode, setCanvassThisMode] = useState(false)

  const hasTurfs = (turfs?.length ?? 0) > 0
  const activeTurfMapId = campaignDetail?.activeTurfMapId
  const showCanvassThis = campaignDetail?.status === 'Active' && !!activeTurfMapId

  const handleViewTurfMap = (turfMapId: string) => {
    setViewTurfMapId(turfMapId)
    turfMapDrawer.onOpen()
  }

  const handleMerged = (_turfMapId: string) => {
    recutModal.onOpen()
  }

  return (
    <VStack gap={6} align="stretch">
      {/* Turf Management */}
      <Box bg="surface" p={6} borderRadius="lg" shadow="md">
        <HStack gap={3} mb={4} justify="space-between">
          <HStack gap={3}>
            <Icon color="accent" asChild><FiMap /></Icon>
            <Text fontWeight="medium">Turf Management</Text>
          </HStack>
          <HStack gap={2} flexWrap="wrap" justify="flex-end">
            {showCanvassThis && (
              <CanvassThisButton
                campaignId={campaignId}
                onReady={() => { setCanvassThisMode(true); recutModal.onOpen() }}
              />
            )}
            {activeTurfMapId && (
              <Button
                size="xs"
                variant="ghost"
                colorPalette="brand"
                onClick={() => handleViewTurfMap(activeTurfMapId)}><FiMap />View Map
                            </Button>
            )}
          </HStack>
        </HStack>

        {/* Generation Change Banner */}
        {review && review.recentGenerationChanges.length > 0 && (
          <Box mb={4}>
            <GenerationChangeBanner
              changes={review.recentGenerationChanges}
              onViewPreviousGeneration={handleViewTurfMap}
            />
          </Box>
        )}

        {/* Active Turfs Table */}
        <Box mb={4}>
          <ActiveTurfsTable turfs={turfs} />
        </Box>

        {/* Action Buttons */}
        <HStack gap={3} flexWrap="wrap">
          {hasTurfs ? (
            <>
              <Button
                size="sm"
                colorPalette="brand"
                variant="outline"
                onClick={mergeListModal.onOpen}><FiPlus />Add Contacts
                              </Button>
              <Button size="sm" colorPalette="brand" onClick={recutModal.onOpen}><FiMap />Review and Update Turfs
                              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                colorPalette="brand"
                variant="outline"
                onClick={() => navigate(`/dashboard/turf-maps/new?campaignId=${campaignId}`)}><FiScissors />Cut Turfs
                              </Button>
              <Button
                size="sm"
                colorPalette="brand"
                variant="outline"
                onClick={autoGenerateModal.onOpen}><FiZap />Auto-Turf
                              </Button>
            </>
          )}
        </HStack>
      </Box>

      {/* Itinerary Builder */}
      <Box bg="surface" p={6} borderRadius="lg" shadow="md">
        <HStack gap={3} mb={4} justify="space-between">
          <HStack gap={3}>
            <Icon color="accent" asChild><FiCalendar /></Icon>
            <Text fontWeight="medium">Itinerary Builder</Text>
          </HStack>
          <Button
            size="sm"
            variant="outline"
            colorPalette="brand"
            onClick={() => navigate(`/dashboard/campaigns/${campaignId}/canvassers`)}><FiUsers />Manage canvassers
                      </Button>
        </HStack>
        <ItineraryBuilder campaignId={campaignId} readOnly={readOnly} />
      </Box>

      {/* Modals */}
      <CampaignAutoGenerateModal
        isOpen={autoGenerateModal.open}
        onClose={autoGenerateModal.onClose}
        campaignId={campaignId}
        campaignLabel={campaignLabel}
        onSuccess={() => {}}
      />
      {activeTurfMapId && (
        <MergeListModal
          isOpen={mergeListModal.open}
          onClose={mergeListModal.onClose}
          turfMapId={activeTurfMapId}
          campaignLabel={campaignLabel}
          onMerged={handleMerged}
        />
      )}
      {review && (
        <TurfReviewMapModal
          isOpen={recutModal.open}
          onClose={() => { recutModal.onClose(); setCanvassThisMode(false) }}
          campaignId={campaignId}
          turfMaps={review.turfMaps}
          turfCoverage={review.turfCoverage}
          reviewDate={todayStr()}
          assignmentMode={campaignDetail?.assignmentMode}
          initialTargetMode={canvassThisMode ? 'today' : undefined}
          canvassThis={canvassThisMode}
          onSaved={canvassThisMode ? () => navigate('/canvasser') : undefined}
        />
      )}

      {/* TurfMap viewer drawer */}
      <TurfMapViewerDrawer
        isOpen={turfMapDrawer.open}
        onClose={() => { turfMapDrawer.onClose(); setViewTurfMapId(null) }}
        turfMapId={viewTurfMapId}
      />
    </VStack>
  );
}
